import { useState, useRef, useLayoutEffect, useCallback } from 'react';
import { cardSrc } from './resultParsers';

// Measured on tenexch.com/admin/casino/ab3 (result modal): owl-rtl, owl-item
// = 38px card + 2px margin-left (25 cards -> stage 1000px), the arrows move
// 3 cards (120px) with a 0.75s slide, and the row can be dragged. The old
// replica paged a whole view at once with no transition, so the next set of
// cards flashed in instead of scrolling, and it could not be dragged.
const SLOT = 40;
const SLIDE_BY = 3;
const SLIDE_MS = 750;

export function ResultOwl({ cards, indices, sliderId }) {
  const outerRef = useRef(null);
  const dragRef = useRef(null);
  const [outerW, setOuterW] = useState(0);
  const [offset, setOffset] = useState(0); // px; positive = scrolled (rtl)
  const [dragDx, setDragDx] = useState(null);

  useLayoutEffect(() => {
    const measure = () => setOuterW(outerRef.current?.clientWidth || 0);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [cards.length]);

  const list = cards;
  const stageWidth = list.length * SLOT;
  // The reference scrolls until the LAST card is the first one in view, so
  // the final step can show a single card (not a full row).
  const maxOffset = Math.max(0, (list.length - 1) * SLOT);
  const cur = Math.min(offset, maxOffset);
  const atStart = cur <= 0;
  const atEnd = cur >= maxOffset;
  const live = dragDx == null ? cur : Math.min(maxOffset, Math.max(0, cur + dragDx));

  const prev = useCallback(() => setOffset((o) => Math.max(0, o - SLIDE_BY * SLOT)), []);
  const next = useCallback(() => setOffset((o) => Math.min(maxOffset, o + SLIDE_BY * SLOT)), [maxOffset]);

  // Drag / swipe (owl-drag): follow the pointer, then snap to a card slot.
  const onPointerDown = (e) => {
    dragRef.current = { x: e.clientX };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDragDx(0);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    // rtl: dragging right reveals the later cards on the left.
    setDragDx(e.clientX - dragRef.current.x);
  };
  const endDrag = () => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setOffset(Math.min(maxOffset, Math.max(0, Math.round(live / SLOT) * SLOT)));
    setDragDx(null);
  };

  // Cards fully inside the visible window are 'active' (owl semantics).
  const firstVis = Math.floor(live / SLOT);
  const lastVis = Math.floor((live + outerW) / SLOT) - 1;

  return (
    <div id={sliderId} className="ab-slider owl-carousel owl-theme owl-rtl owl-loaded owl-drag">
      <div
        className="owl-stage-outer"
        ref={outerRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        style={{ touchAction: 'pan-y' }}
      >
        <div
          className="owl-stage"
          style={{
            transform: `translate3d(${live}px, 0px, 0px)`,
            transition: dragDx == null ? `${SLIDE_MS / 1000}s` : 'none',
            width: `${stageWidth}px`,
          }}
        >
          {list.map((code, i) => {
            const active = i >= firstVis && i <= lastVis;
            return (
              <div key={i} className={`owl-item${active ? ' active' : ''}`} style={{ width: 'auto', marginLeft: '2px' }}>
                <div className="casino-result-cards-item item">
                  <div className="text-center">{indices[i]}</div>
                  {' '}
                  <img src={cardSrc(code)} alt={code} draggable={false} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className={`owl-nav${list.length === 0 ? ' disabled' : ''}`}>
        <button type="button" role="presentation" className={`owl-prev${atStart ? ' disabled' : ''}`} onClick={atStart ? undefined : prev}>
          <span aria-label="Previous">‹</span>
        </button>
        <button type="button" role="presentation" className={`owl-next${atEnd ? ' disabled' : ''}`} onClick={atEnd ? undefined : next}>
          <span aria-label="Next">›</span>
        </button>
      </div>
      <div className="owl-dots disabled"></div>
    </div>
  );
}

// ANDAR BAHAR 50 CARDS result — col-lg-8 me d-flex: abj-title (ANDAR/BAHAR labels)
// + col-9 me do card-inner (andarSlider/baharSlider owl). Andar = odd indices
// (deal position 2,4,..,50), Bahar = even indices (1,3,..,49). col-lg-4 me Winner.
export default function AB3Result({ detail }) {
  const allCards = (detail?.card || '').split(',').map((c) => c.trim()).filter(Boolean);
  const andar = allCards.filter((_, i) => i % 2 === 1);
  const bahar = allCards.filter((_, i) => i % 2 === 0);
  const andarIdx = andar.map((_, i) => i * 2 + 2);
  const baharIdx = bahar.map((_, i) => i * 2 + 1);
  const winner = detail?.winnat || detail?.rdesc || 'Win';

  return (
    <div className="row row5 ab2-result">
      <div className="col-12 col-lg-8">
        <div className="d-flex">
          <div className="mr-3 abj-title">
            <div className="row row5"><div className="col-12"><b>ANDAR</b></div></div>
            {' '}
            <div className="row row5"><div className="col-12"><b>BAHAR</b></div></div>
          </div>
          {' '}
          <div className="col-9">
            <div className="card-inner">
              <div className="row row5">
                <div className="col-10">
                  <ResultOwl cards={andar} indices={andarIdx} sliderId="andarSlider" />
                </div>
                {' '}
                <div className="col-2"></div>
              </div>
            </div>
            {' '}
            <div className="card-inner">
              <div className="row row5">
                <div className="col-10">
                  <ResultOwl cards={bahar} indices={baharIdx} sliderId="baharSlider" />
                </div>
                {' '}
                <div className="col-2"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="col-12 col-lg-4">
        <div className="casino-result-desc">
          <div className="casino-result-desc-item">
            <div>Winner <span className="float-right">:</span></div>
            {' '}
            <div>{winner}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
